import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImgIcon, Trash2, ArrowLeft, ArrowRight, Star } from "lucide-react";

/**
 * CollectionPhotos — gallery management for a single collection.
 * Supports: upload (click or drag/drop), reorder (← →), set-as-cover, delete.
 * All photos are base64 data URLs, max 8 MB each, max 12 per collection.
 */
const MAX = 12;
const MAX_BYTES = 8 * 1024 * 1024;

export default function CollectionPhotos({ collection, onChange }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const photos = collection?.gallery_photos || [];
  const cover = collection?.cover_image || null;
  const atLimit = photos.length >= MAX;

  async function uploadFile(f) {
    if (!f) return;
    if (!f.type?.startsWith("image/")) { toast.error("that's not an image"); return; }
    if (f.size > MAX_BYTES) { toast.error("photo too large (max 8 MB)"); return; }
    const dataUrl = await fileToDataUrl(f);
    setBusy(true);
    try {
      const r = await api.post(`/collections/${collection.id}/photos`, { data_url: dataUrl });
      onChange({ gallery_photos: r.data.gallery_photos, cover_image: r.data.cover_image });
      toast.success("photo added");
    } catch (e) {
      console.error("[collection-photos] upload failed:", e);
      toast.error(e?.response?.data?.detail || "could not add photo");
    } finally {
      setBusy(false);
    }
  }

  async function move(idx, dir) {
    if (busy) return;
    const next = idx + dir;
    if (next < 0 || next >= photos.length) return;
    const order = photos.map((_, i) => i);
    [order[idx], order[next]] = [order[next], order[idx]];
    setBusy(true);
    try {
      const r = await api.patch(`/collections/${collection.id}/photos/order`, { order });
      onChange({ gallery_photos: r.data.gallery_photos });
    } catch (e) {
      console.error("[collection-photos] reorder failed:", e);
      toast.error("could not reorder");
    } finally { setBusy(false); }
  }

  async function makeCover(idx) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.patch(`/collections/${collection.id}/cover`, { index: idx });
      onChange({ cover_image: r.data.cover_image });
      toast.success("cover updated");
    } catch (e) {
      console.error("[collection-photos] cover failed:", e);
      toast.error("could not update cover");
    } finally { setBusy(false); }
  }

  async function remove(idx) {
    if (busy) return;
    if (!window.confirm("remove this photo?")) return;
    setBusy(true);
    try {
      const r = await api.delete(`/collections/${collection.id}/photos/${idx}`);
      onChange({ gallery_photos: r.data.gallery_photos, cover_image: r.data.cover_image });
      toast.success("photo removed");
    } catch (e) {
      console.error("[collection-photos] delete failed:", e);
      toast.error("could not remove");
    } finally { setBusy(false); }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) uploadFile(f);
  }

  return (
    <section className="placed-card p-6" data-testid="collection-photos">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <ImgIcon size={16} strokeWidth={1.4} className="text-[var(--bronze)]" />
          <h2 className="font-serif-lit text-2xl text-[var(--ink)]">Photos</h2>
        </div>
        <span className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-light)]">
          {photos.length} / {MAX}
        </span>
      </header>

      {photos.length === 0 ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="collection-photos-empty"
          className={`cursor-pointer rounded border-2 border-dashed p-10 text-center transition-colors ${
            dragOver
              ? "border-[var(--bronze)] bg-[var(--paper-aged)]"
              : "border-[rgba(140,106,47,0.32)] hover:border-[var(--bronze)] hover:bg-[var(--paper-aged)]/50"
          }`}
        >
          <ImgIcon size={24} strokeWidth={1.4} className="mx-auto text-[var(--bronze)]" />
          <p className="mt-3 font-serif-lit text-lg text-[var(--ink)]">drop a photo here</p>
          <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
            or click to browse — up to 12 photos, 8 MB each
          </p>
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="collection-photos-grid">
            {photos.map((src, idx) => {
              const isCover = src === cover;
              return (
                <li key={idx} className="relative group aspect-square rounded overflow-hidden border border-[rgba(42,30,20,0.1)]" data-testid={`collection-photo-${idx}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  {isCover && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--card-bg)]/92 backdrop-blur-sm border border-[rgba(42,30,20,0.16)] font-cinzel text-[8px] tracking-[0.18em] uppercase text-[var(--bronze)]">
                      <Star size={9} strokeWidth={2} fill="currentColor" /> cover
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={busy || idx === 0}
                      aria-label="move left"
                      data-testid={`collection-photo-left-${idx}`}
                      className="w-7 h-7 rounded-full bg-white/90 text-[var(--ink)] flex items-center justify-center hover:bg-white disabled:opacity-30"
                    >
                      <ArrowLeft size={12} />
                    </button>
                    {!isCover && (
                      <button
                        type="button"
                        onClick={() => makeCover(idx)}
                        disabled={busy}
                        aria-label="set as cover"
                        data-testid={`collection-photo-cover-${idx}`}
                        className="w-7 h-7 rounded-full bg-white/90 text-[var(--ink)] flex items-center justify-center hover:bg-white"
                      >
                        <Star size={12} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      disabled={busy}
                      aria-label="remove photo"
                      data-testid={`collection-photo-remove-${idx}`}
                      className="w-7 h-7 rounded-full bg-white/90 text-[#9E3B2B] flex items-center justify-center hover:bg-white"
                    >
                      <Trash2 size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={busy || idx === photos.length - 1}
                      aria-label="move right"
                      data-testid={`collection-photo-right-${idx}`}
                      className="w-7 h-7 rounded-full bg-white/90 text-[var(--ink)] flex items-center justify-center hover:bg-white disabled:opacity-30"
                    >
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy || atLimit}
              data-testid="collection-photo-add-btn"
              className="lc-btn-ghost inline-flex items-center gap-2"
            >
              <ImgIcon size={14} strokeWidth={1.5} />
              {atLimit ? "max photos reached" : "add a photo"}
            </button>
          </div>
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        data-testid="collection-photo-input"
        onChange={(e) => uploadFile(e.target.files?.[0])}
      />
    </section>
  );
}

function fileToDataUrl(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });
}
